import React, { useEffect, useState, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { useDispatch } from 'react-redux';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";
import { fetchStepsTrackerStats } from "./store";

const StepsTrackerChart = () => {
    const dispatch = useDispatch();
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('daily');
    const dataFetchedRef = useRef(false);

    const fetchData = (selectedTimeframe) => {
        setLoading(true);
        const cacheKey = `STEPS_TRACKER_CACHE_${selectedTimeframe.toUpperCase()}`;

        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') return;

            const cacheTimestamp = data.cacheTimestamp || 0;
            const threeMinutes = 3 * 60 * 1000;
            const isExpired = Date.now() - cacheTimestamp > threeMinutes;
            if (!isExpired) {
                setChartData(data.data);
                setLoading(false);
                return;
            } else {
                sessionStorage.removeItem(cacheKey);
            }
        }

        // Mark as pending
        sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

        dispatch(fetchStepsTrackerStats(selectedTimeframe))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const fetchedData = response.payload.data;
                    setChartData(fetchedData);
                    sessionStorage.setItem(cacheKey, JSON.stringify({ data: fetchedData, cacheTimestamp: Date.now() }));
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching steps tracker stats:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (dataFetchedRef.current && timeframe === 'daily') return;
        if (timeframe === 'daily') dataFetchedRef.current = true;

        fetchData(timeframe);
    }, [dispatch, timeframe]);

    return (
        <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            flex: '1',
            minWidth: '380px',
            height: '450px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '4px', height: '20px', background: '#10b981', borderRadius: '2px' }}></div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>Steps Tracker</h3>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {['daily', 'weekly', 'monthly'].map((tf) => (
                        <button
                            key={tf}
                            onClick={() => setTimeframe(tf)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                background: timeframe === tf ? '#10b981' : '#f9fafb',
                                color: timeframe === tf ? '#fff' : '#4b5563',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                transition: 'all 0.2s'
                            }}
                        >
                            {tf === 'daily' ? '7 Days' : tf}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div style={{ height: '313.8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ComponentLoader color="#10b981" size="40px" />
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={313.8}>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis
                            dataKey="period"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            dy={10}
                            tickFormatter={(value) => {
                                if (timeframe === 'daily') return value.split('-').slice(1).join('/');
                                return value;
                            }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            label={{ value: 'Active Users', angle: -90, position: 'insideLeft', offset: 10, fill: '#9ca3af', fontSize: 10 }}
                        />
                        <Tooltip
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                            type="monotone"
                            dataKey="Female"
                            stroke="#EC4899"
                            strokeWidth={3}
                            dot={{ r: 4, fill: '#EC4899' }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="Male"
                            stroke="#3B82F6"
                            strokeWidth={3}
                            dot={{ r: 4, fill: '#3B82F6' }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="Other"
                            stroke="#9CA3AF"
                            strokeWidth={3}
                            strokeDasharray="5 5"
                            dot={{ r: 4, fill: '#9CA3AF' }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default StepsTrackerChart;
